import React, { createContext, useContext, useEffect, useState } from 'react';
import { DEFAULT_THEME, themePresets } from '../themes';

const STORAGE_KEY = 'helical:theme';
const COLOR_MODE_KEY = 'helical:colorMode';

const validThemeNames = new Set(themePresets.map(p => p.name));

function readStoredTheme(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && validThemeNames.has(stored)) {
      return stored;
    }
  } catch {
    /* localStorage unavailable */
  }
  return DEFAULT_THEME;
}

function readStoredColorMode(): 'night' | 'day' {
  try {
    const stored = localStorage.getItem(COLOR_MODE_KEY);
    if (stored === 'day' || stored === 'night') return stored;
  } catch {
    /* localStorage unavailable */
  }
  return 'night';
}

function applyThemeClass(theme: string): void {
  const body = document.body;
  const toRemove: string[] = [];
  body.classList.forEach(cls => { if (cls.startsWith('theme-')) toRemove.push(cls); });
  toRemove.forEach(cls => body.classList.remove(cls));
  body.classList.add(`theme-${theme}`);
}

function applyColorModeClass(mode: 'night' | 'day'): void {
  if (mode === 'day') {
    document.body.classList.add('day-mode');
  } else {
    document.body.classList.remove('day-mode');
  }
  window.dispatchEvent(new CustomEvent('helical:colorModeChange', { detail: { colorMode: mode } }));
}

interface ActiveThemeContextValue {
  activeTheme: string;
  setActiveTheme: (theme: string) => void;
  colorMode: 'night' | 'day';
  setColorMode: (mode: 'night' | 'day') => void;
}

const ActiveThemeContext = createContext<ActiveThemeContextValue>({
  activeTheme: DEFAULT_THEME,
  setActiveTheme: () => {},
  colorMode: 'night',
  setColorMode: () => {},
});

interface ActiveThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: string;
}

export function ActiveThemeProvider({
  children,
  defaultTheme = DEFAULT_THEME,
}: ActiveThemeProviderProps) {
  const [activeTheme, setActiveThemeState] = useState<string>(() => {
    const stored = readStoredTheme();
    return stored !== DEFAULT_THEME ? stored : defaultTheme;
  });

  const [colorMode, setColorModeState] = useState<'night' | 'day'>(readStoredColorMode);

  // Apply theme class on mount and on every change
  useEffect(() => {
    applyThemeClass(activeTheme);
  }, [activeTheme]);

  // Apply color mode class on mount and on every change
  useEffect(() => {
    applyColorModeClass(colorMode);
  }, [colorMode]);

  const setActiveTheme = (theme: string) => {
    if (!validThemeNames.has(theme)) return;
    setActiveThemeState(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  };

  const setColorMode = (mode: 'night' | 'day') => {
    setColorModeState(mode);
    try {
      localStorage.setItem(COLOR_MODE_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  return (
    <ActiveThemeContext.Provider value={{ activeTheme, setActiveTheme, colorMode, setColorMode }}>
      {children}
    </ActiveThemeContext.Provider>
  );
}

export function useActiveTheme(): ActiveThemeContextValue {
  return useContext(ActiveThemeContext);
}

export function useColorMode(): Pick<ActiveThemeContextValue, 'colorMode' | 'setColorMode'> {
  const { colorMode, setColorMode } = useContext(ActiveThemeContext);
  return { colorMode, setColorMode };
}
